import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Occupancy Type master (Masters → Occupancy Types). Typed split out of legacy
 * `tbl_MiscellaneousMaster` where `FieldFilter = 'OccupancyType'`. Common
 * codes: SGL (single, 1 pax), DBL (double, 2), TPL (triple, 3), QUAD (4),
 * CWB (child with bed), CNB (child no bed). Linked from `itinerary_components`
 * for HOTEL components.
 */

export const OccupancyTypeListItemSchema = z.object({
  id: UuidSchema,
  legacyLookupId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  paxCount: z.number().int().min(1),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type OccupancyTypeListItem = z.infer<typeof OccupancyTypeListItemSchema>;

export const OccupancyTypeListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type OccupancyTypeListQuery = z.infer<typeof OccupancyTypeListQuerySchema>;

export const OccupancyTypeListResponseSchema = paginatedResponseSchema(OccupancyTypeListItemSchema);
export type OccupancyTypeListResponse = z.infer<typeof OccupancyTypeListResponseSchema>;

export const OccupancyTypeDetailSchema = OccupancyTypeListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type OccupancyTypeDetail = z.infer<typeof OccupancyTypeDetailSchema>;

const OccupancyTypeCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  paxCount: z.number().int().min(1, 'Pax count must be at least 1').max(20).optional(),
  legacyLookupId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateOccupancyTypeRequestSchema = OccupancyTypeCoreFieldsSchema;
export type CreateOccupancyTypeRequest = z.infer<typeof CreateOccupancyTypeRequestSchema>;

export const UpdateOccupancyTypeRequestSchema = OccupancyTypeCoreFieldsSchema.partial();
export type UpdateOccupancyTypeRequest = z.infer<typeof UpdateOccupancyTypeRequestSchema>;
